/**
 * Canonical, per-runtime demolition events.
 *
 * The ring owns both the capped evaluation history and the presentation queue.
 * They contain the same immutable record references, so draining presentation
 * work never removes facts needed by evaluation.
 */

import type { DemolitionWeapon } from '../../services/damageModel';
import type { DamageFact } from './demolitionDamageLedger';
import type { ProfileId } from '../../services/destructionProfiles';

export type DemolitionEventKind =
  | 'impact'
  | 'impact-burst'
  | 'shockwave'
  | 'shard'
  | 'impact-trace'
  | 'damage-fact';

export interface DemolitionVector3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface DemolitionImpactPayload {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly speed: number;
}

export interface DemolitionImpactBurstPayload {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly radius: number;
  readonly normal: DemolitionVector3;
  readonly weapon: DemolitionWeapon;
  readonly material: ProfileId;
  /** Crushed mass for this burst (m³). 0 when the hit produced no powder. */
  readonly pulverizedVolume?: number;
}

export interface DemolitionShockwavePayload {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly radius: number;
}

export interface DemolitionShardPayload {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly vx: number;
  readonly vy: number;
  readonly vz: number;
  readonly volume: number;
  readonly material: ProfileId;
}

export type DemolitionImpactContactKind = 'monolith' | 'static' | 'dynamic' | 'projectile' | 'ground';

export interface DemolitionImpactRelease {
  readonly id: number;
  readonly position: DemolitionVector3;
  /** Launch velocity applied at release; zeros when none. Never NaN. */
  readonly vx: number;
  readonly vy: number;
  readonly vz: number;
  readonly speed: number;
}

export interface DemolitionImpactTracePayload {
  readonly projectileBodyId?: number;
  readonly contactBodyId?: number;
  readonly contactKind: DemolitionImpactContactKind;
  readonly contactPoint: DemolitionVector3;
  readonly approachSpeed: number;
  readonly projectileRadius: number;
  readonly projectileMass: number;
  readonly energyTransfer: number;
  readonly appliedEnergy: number;
  readonly rubbleKickSpeed?: number;
  readonly transmitted?: true;
  /** Spike stuck in structure instead of bouncing. */
  readonly embedded?: true;
  /** Feedback-only contact below the structural speed gate (never damaging). */
  readonly belowStructuralGate?: true;
  readonly preferredPrimaryCellId?: number;
  readonly damageOrigin: DemolitionVector3;
  readonly directReleases: readonly DemolitionImpactRelease[];
  readonly supportReleases: readonly DemolitionImpactRelease[];
  readonly carvedCellCount: number;
  readonly topologyChanged: boolean;
  readonly weapon: DemolitionWeapon;
  readonly material: ProfileId;
}

export interface DemolitionEventPayloadMap {
  readonly impact: DemolitionImpactPayload;
  readonly 'impact-burst': DemolitionImpactBurstPayload;
  readonly shockwave: DemolitionShockwavePayload;
  readonly shard: DemolitionShardPayload;
  readonly 'impact-trace': DemolitionImpactTracePayload;
  readonly 'damage-fact': DamageFact;
}

export type DeepReadonly<T> = T extends (...args: never[]) => unknown
  ? T
  : T extends readonly (infer U)[]
    ? ReadonlyArray<DeepReadonly<U>>
    : T extends object
      ? { readonly [K in keyof T]: DeepReadonly<T[K]> }
      : T;

export interface CanonicalDemolitionEvent<K extends DemolitionEventKind = DemolitionEventKind> {
  /** Reset epoch; sequences are only unique within one epoch. */
  readonly epoch: number;
  readonly sequence: number;
  readonly kind: K;
  readonly payload: DeepReadonly<DemolitionEventPayloadMap[K]>;
}

/** Discriminated union used by pull-drain consumers. */
export type CanonicalDemolitionEventUnion = {
  [K in DemolitionEventKind]: CanonicalDemolitionEvent<K>;
}[DemolitionEventKind];

export interface DemolitionEventRingOptions {
  /** Maximum number of records retained by either history or pending queue. */
  readonly capacity?: number;
}

export interface DemolitionEventRingTelemetry {
  readonly epoch: number;
  readonly appended: number;
  readonly droppedHistory: number;
  readonly droppedPending: number;
  readonly retainedHistoryStart: number | null;
  readonly retainedHistoryEnd: number | null;
  readonly pendingStart: number | null;
  readonly pendingEnd: number | null;
}

export interface DemolitionEventRing {
  readonly capacity: number;
  readonly epoch: number;
  append<K extends DemolitionEventKind>(
    kind: K,
    payload: DemolitionEventPayloadMap[K],
  ): CanonicalDemolitionEvent<K>;
  recent(): readonly CanonicalDemolitionEventUnion[];
  drain(): readonly CanonicalDemolitionEventUnion[];
  telemetry(): DemolitionEventRingTelemetry;
  reset(): void;
}

const DEFAULT_CAPACITY = 64;

/** Clone data payloads before freezing them so the caller retains ownership of its input. */
function cloneAndFreeze<T>(value: T, seen = new WeakMap<object, unknown>()): T {
  if (value === null || typeof value !== 'object') return value;

  const source = value as object;
  const existing = seen.get(source);
  if (existing !== undefined) return existing as T;

  if (Array.isArray(value)) {
    const copy: unknown[] = [];
    seen.set(source, copy);
    for (const item of value) copy.push(cloneAndFreeze(item, seen));
    return Object.freeze(copy) as T;
  }

  const copy: Record<PropertyKey, unknown> = {};
  seen.set(source, copy);
  for (const key of Reflect.ownKeys(source)) {
    const descriptor = Object.getOwnPropertyDescriptor(source, key);
    if (descriptor && 'value' in descriptor) copy[key] = cloneAndFreeze(descriptor.value, seen);
  }
  return Object.freeze(copy) as T;
}

function checkedCapacity(options?: DemolitionEventRingOptions): number {
  const capacity = options?.capacity ?? DEFAULT_CAPACITY;
  if (!Number.isSafeInteger(capacity) || capacity < 1) {
    throw new RangeError('Demolition event ring capacity must be a positive safe integer');
  }
  return capacity;
}

/** Create an isolated canonical event ring for one demolition runtime. */
export function createDemolitionEventRing(options?: DemolitionEventRingOptions): DemolitionEventRing {
  const capacity = checkedCapacity(options);
  let epoch = 0;
  let nextSequence = 0;
  let appended = 0;
  let droppedHistory = 0;
  let droppedPending = 0;
  let history: CanonicalDemolitionEventUnion[] = [];
  let pending: CanonicalDemolitionEventUnion[] = [];

  const trim = (records: CanonicalDemolitionEventUnion[]) => {
    if (records.length > capacity) records.splice(0, records.length - capacity);
  };

  return {
    capacity,

    get epoch() {
      return epoch;
    },

    append<K extends DemolitionEventKind>(
      kind: K,
      payload: DemolitionEventPayloadMap[K],
    ): CanonicalDemolitionEvent<K> {
      nextSequence += 1;
      appended += 1;
      const record = Object.freeze({
        epoch,
        sequence: nextSequence,
        kind,
        payload: cloneAndFreeze(payload),
      }) as unknown as CanonicalDemolitionEventUnion;

      history.push(record);
      pending.push(record);
      if (history.length > capacity) {
        droppedHistory += history.length - capacity;
        trim(history);
      }
      if (pending.length > capacity) {
        droppedPending += pending.length - capacity;
        trim(pending);
      }
      return record as CanonicalDemolitionEvent<K>;
    },

    recent(): readonly CanonicalDemolitionEventUnion[] {
      return Object.freeze(history.slice());
    },

    drain(): readonly CanonicalDemolitionEventUnion[] {
      const drained = pending;
      pending = [];
      return Object.freeze(drained);
    },

    telemetry(): DemolitionEventRingTelemetry {
      const telemetry = {
        epoch,
        appended,
        droppedHistory,
        droppedPending,
        retainedHistoryStart: history[0]?.sequence ?? null,
        retainedHistoryEnd: history[history.length - 1]?.sequence ?? null,
        pendingStart: pending[0]?.sequence ?? null,
        pendingEnd: pending[pending.length - 1]?.sequence ?? null,
      };
      return Object.freeze(telemetry);
    },

    reset(): void {
      epoch += 1;
      nextSequence = 0;
      appended = 0;
      droppedHistory = 0;
      droppedPending = 0;
      history = [];
      pending = [];
    },
  };
}
